// src/components/pages/client/ClientBooking.tsx

import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Phone,
  MapPin,
  Loader2,
  ArrowLeft,
  LogOut,
  Check,
  ChevronRight,
  ChevronDown,
  Info,
  Minus,
  Plus,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { useEmpresa } from '@/components/hooks/useEmpresa';
import { useTrabajadoresPorServicio } from '@/components/hooks/useTrabajadorDetalles';
import { useServiciosPorEmpresa } from '@/components/hooks/useServicios';
import {
  useCitasPendientesPorCliente,
  haySolape,
} from '@/components/hooks/useCitas';
import { useConsentimientoCliente } from '@/components/hooks/useConsentimientoCliente';
import { useProfiles } from '@/components/hooks/useProfiles';
import { usePushToken } from '@/components/hooks/usePushToken';
import { logoutUser } from '@/components/utils/logoutUser';
import { openExternalLink } from '@/components/utils/openExternalLink';
import EmpresaNoDisponible from '@/components/pages/EmpresaNoDisponible';
import EmpresaLoader from '@/components/pages/shared/EmpresaLoader';
import {
  useObtenerHorariosPorRango,
  HorarioItem,
} from '@/components/hooks/useObtenerHorarios';
import { crearCita } from '@/components/hooks/useCitas';
import { addDays, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Switch } from '@/components/ui/switch';

import LoginDialog from '@/components/pages/dialogs/LoginDialog';
import ClientRegisterChoiceDialog from '@/components/pages/dialogs/ClientRegisterChoiceDialog';

// ─── hoyISO se calcula una vez al montar (no al cargar el módulo)
const getHoyISO = () => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const año = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const dia = String(hoy.getDate()).padStart(2, '0');
  return `${año}-${mes}-${dia}`;
};

const generarProximosDias = (cantidad: number, hoyISO: string) => {
  const hoy = new Date(hoyISO + 'T00:00:00');
  return Array.from({ length: cantidad }, (_, i) => {
    const fecha = new Date(hoy);
    fecha.setDate(hoy.getDate() + i);
    const fechaISO = [
      fecha.getFullYear(),
      String(fecha.getMonth() + 1).padStart(2, '0'),
      String(fecha.getDate()).padStart(2, '0'),
    ].join('-');
    return {
      fechaISO,
      diaSemana: format(fecha, 'EEE', { locale: es }),
      diaNumero: format(fecha, 'd', { locale: es }),
    };
  });
};

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepDot = ({
  active,
  done,
  label,
}: {
  active: boolean;
  done: boolean;
  label: string;
}) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
        done
          ? 'bg-[#103254] text-white'
          : active
          ? 'bg-[#103254]/20 dark:bg-blue-400/20 text-[#103254] dark:text-blue-300 ring-2 ring-[#103254] dark:ring-blue-400'
          : 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-500'
      }`}
    >
      {done ? <Check className="w-3 h-3" /> : null}
    </div>
    <span
      className={`text-[9px] uppercase tracking-wider font-semibold ${
        active || done
          ? 'text-[#103254] dark:text-blue-400'
          : 'text-gray-400 dark:text-gray-500'
      }`}
    >
      {label}
    </span>
  </div>
);

const StepLine = ({ done }: { done: boolean }) => (
  <div
    className={`flex-1 h-px mt-3 transition-all duration-500 ${
      done ? 'bg-[#103254] dark:bg-blue-400' : 'bg-gray-200 dark:bg-white/10'
    }`}
  />
);

const ClientBooking = () => {
  const { id_empresa } = useParams<{ id_empresa: string }>();
  const navigate = useNavigate();

  const canGoBack = (window.history.state?.idx ?? 0) > 0;

  // hoyISO calculado una sola vez al montar el componente
  const hoyISO = useMemo(() => getHoyISO(), []);

  const initialFormState = useMemo(
    () => ({
      trabajador: null as string | null,
      servicio: '',
      telefono: '',
      email: '',
      nombre: '',
      fecha: hoyISO,
      hora: '',
      recurso_numero: null as number | null,
      consentimiento: false,
      num_clientes: 1,
      nota: undefined as string | undefined,
    }),
    [hoyISO]
  );

  const [form, setForm] = useState(initialFormState);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [citaRegistrada, setCitaRegistrada] = useState(false);

  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { empresa, loadingEmpresa, errorEmpresa } = useEmpresa(id_empresa);

  // Logo en la pantalla de carga: viene de CompanyDetail por la navegación
  // o, si se entra directo, de una consulta ligera.
  const navState = useLocation().state as {
    logo?: string | null;
    nombre?: string;
  } | null;
  const [logoPreview, setLogoPreview] = useState<{
    logo: string | null;
    nombre: string;
  } | null>(
    navState?.logo
      ? { logo: navState.logo, nombre: navState.nombre ?? '' }
      : null
  );
  useEffect(() => {
    if (logoPreview || !id_empresa) return;
    supabase
      .from('empresas')
      .select('logo, empresa')
      .eq('id_empresa', id_empresa)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setLogoPreview({ logo: data.logo, nombre: data.empresa });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_empresa]);

  const [splashDone, setSplashDone] = useState(false);
  const [logoCargado, setLogoCargado] = useState(false);
  useEffect(() => {
    if (loadingEmpresa || !empresa) return;
    if (!empresa.logo) {
      setSplashDone(true);
      return;
    }
    if (!logoCargado) return;
    const id = setTimeout(() => setSplashDone(true), 800);
    return () => clearTimeout(id);
  }, [empresa, loadingEmpresa, logoCargado]);
  const { registrarPushToken } = usePushToken();

  // ── Meta tags dinámicos para título del navegador ─────────────────────────
  useEffect(() => {
    if (!empresa) return;
    const nombre = empresa.empresa ?? 'Reserva tu cita';
    const ciudad = [
      ...new Set([empresa.localidad, empresa.ciudad].filter(Boolean)),
    ].join(', ');
    const title = `${nombre}${
      ciudad ? ` — ${ciudad}` : ''
    } | Reserva ahora · Bookoll`;
    const description = `Reserva tu cita en ${nombre}${
      ciudad ? `, ${ciudad}` : ''
    } de forma rápida y sencilla. Disponible 24h en Bookoll.`;
    const image =
      typeof empresa.logo === 'string' && empresa.logo.trim() !== ''
        ? empresa.logo
        : 'https://bookoll.com/og-home.jpg';

    document.title = title;

    const setMeta = (
      property: string,
      attrName: 'name' | 'property',
      value: string
    ) => {
      let el = document.querySelector(
        `meta[${attrName}="${property}"]`
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    setMeta('description', 'name', description);
    setMeta('og:title', 'property', title);
    setMeta('og:description', 'property', description);
    setMeta('og:image', 'property', image);
    setMeta('og:url', 'property', window.location.href);
    setMeta('twitter:title', 'name', title);
    setMeta('twitter:description', 'name', description);
    setMeta('twitter:image', 'name', image);

    Sentry.setContext('empresa', {
      id_empresa,
      nombre: empresa.empresa,
      ciudad: empresa.ciudad,
    });

    return () => {
      document.title = 'Bookoll | Gestión de reservas y agendas para empresas';
      Sentry.setContext('empresa', null);
    };
  }, [empresa, id_empresa]);

  const { trabajadores } = useTrabajadoresPorServicio(
    form.servicio,
    id_empresa
  );
  const { perfiles, loading: loadingPerfiles, refetch } = useProfiles();

  const diasVisibles = empresa?.dias_visibles_reserva ?? 14;

  const diasDisponibles = useMemo(
    () => generarProximosDias(diasVisibles, hoyISO),
    [diasVisibles, hoyISO]
  );

  const { servicios: serviciosPublicos } = useServiciosPorEmpresa(
    id_empresa || '',
    true,
    true
  );
  // Los públicos no reservables online solo se muestran en la ficha.
  const servicios = useMemo(
    () => serviciosPublicos.filter((s) => s.reservable_online !== false),
    [serviciosPublicos]
  );

  // Agrupados por categoría (orden de la categoría); los que no tienen van
  // al final. Dentro de cada grupo se respeta el orden que ya traían.
  const serviciosOrdenados = useMemo(
    () =>
      [...servicios].sort((a, b) => {
        const ca = a.categoria_servicio;
        const cb = b.categoria_servicio;
        if (!ca || !cb) return ca ? -1 : cb ? 1 : 0;
        if (ca.id_categoria_servicio === cb.id_categoria_servicio) return 0;
        return (
          (ca.orden ?? 0) - (cb.orden ?? 0) || ca.nombre.localeCompare(cb.nombre)
        );
      }),
    [servicios]
  );
  const agruparPorCategoria =
    new Set(
      servicios.map((s) => s.categoria_servicio?.id_categoria_servicio ?? null)
    ).size > 1;

  const gruposServicios = useMemo(() => {
    const grupos: {
      clave: string;
      nombre: string;
      servicios: typeof serviciosOrdenados;
    }[] = [];
    serviciosOrdenados.forEach((s) => {
      const clave = s.categoria_servicio?.id_categoria_servicio ?? 'sin-categoria';
      let g = grupos.find((x) => x.clave === clave);
      if (!g) {
        g = {
          clave,
          nombre: s.categoria_servicio?.nombre ?? 'Otros servicios',
          servicios: [],
        };
        grupos.push(g);
      }
      g.servicios.push(s);
    });
    return grupos;
  }, [serviciosOrdenados]);
  const [categoriasAbiertas, setCategoriasAbiertas] = useState<
    Record<string, boolean>
  >({});
  const servicioSeleccionado = servicios.find(
    (s) => s.id_servicio === form.servicio
  );

  const porHoras = !!servicioSeleccionado?.reserva_por_horas;
  const horasMin = servicioSeleccionado?.horas_min ?? 1;
  const horasMax = servicioSeleccionado?.horas_max ?? horasMin;
  const [horasReserva, setHorasReserva] = useState(1);
  useEffect(() => {
    setHorasReserva(horasMin);
  }, [form.servicio, horasMin]);
  const duracionReserva = porHoras
    ? horasReserva * 60
    : servicioSeleccionado?.duracion_minutos ?? 0;
  const precioTotalReserva =
    (servicioSeleccionado?.precio ?? 0) *
    (servicioSeleccionado?.precio_aplica === 'Por persona'
      ? form.num_clientes
      : 1) *
    (porHoras ? horasReserva : 1);

  const cambiarHoras = (delta: number) => {
    const n = Math.min(horasMax, Math.max(horasMin, horasReserva + delta));
    if (n === horasReserva) return;
    setHorasReserva(n);
  };

  // ── Una sola llamada RPC para todo el rango ───────────────────────────────
  const rangoInicio =
    servicioSeleccionado?.requiere_trabajador && !form.trabajador ? '' : hoyISO;
  const rangoFin =
    servicioSeleccionado?.requiere_trabajador && !form.trabajador
      ? ''
      : diasDisponibles[diasDisponibles.length - 1]?.fechaISO ?? '';

  const { horariosPorDia } = useObtenerHorariosPorRango(
    servicioSeleccionado?.requiere_trabajador ? form.trabajador || null : null,
    rangoInicio,
    rangoFin,
    form.servicio || undefined,
    porHoras ? duracionReserva : null
  );

  // Las horas del día seleccionado se derivan directamente del rango — sin llamada adicional
  const horasDisponibles: HorarioItem[] = horariosPorDia[form.fecha] ?? [];

  // Horario nocturno: las horas del día siguiente antes de las 06:00 son la
  // continuación de esta noche; se ofrecen también desde este día.
  const fechaSiguiente = form.fecha
    ? format(addDays(new Date(`${form.fecha}T00:00:00`), 1), 'yyyy-MM-dd')
    : '';
  const horasMadrugada = useMemo(() => {
    const ultimaHoy = horasDisponibles.reduce(
      (max, h) => (h.hora > max ? h.hora : max),
      ''
    );
    if (!ultimaHoy || ultimaHoy < '18:00') return [];
    const porHora: Record<string, HorarioItem[]> = {};
    (horariosPorDia[fechaSiguiente] ?? [])
      .filter((h) => h.hora < '06:00')
      .forEach((h) => {
        if (!porHora[h.hora]) porHora[h.hora] = [];
        porHora[h.hora].push(h);
      });
    return Object.entries(porHora).sort(([a], [b]) => a.localeCompare(b));
  }, [horasDisponibles, horariosPorDia, fechaSiguiente]);
  const [verMadrugada, setVerMadrugada] = useState(false);

  // Al cambiar la duración se recalculan los huecos: si la hora elegida ya no
  // cabe, se quita.
  useEffect(() => {
    if (!porHoras || !form.hora) return;
    const sigue = (horariosPorDia[form.fecha] ?? []).some(
      (h) => h.hora === form.hora
    );
    if (!sigue) {
      setForm((prev) => ({ ...prev, hora: '' }));
      toast.info(
        `A esa hora no caben ${horasReserva} h. Elige otra hora.`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horariosPorDia]);

  // En servicios de grupo el mínimo se alcanza entre varias reservas, así que
  // cada una puede ser de 1; sin grupos, el mínimo es por reserva.
  const participantesMin = servicioSeleccionado?.permite_grupos
    ? 1
    : servicioSeleccionado?.cliente_min || 1;
  // Plazas que quedan en la hora elegida (servicios de grupo).
  const plazasHora = horasDisponibles.find(
    (h) =>
      h.hora === form.hora &&
      (form.recurso_numero == null || h.recurso_numero === form.recurso_numero)
  )?.plazas_disponibles;
  // Grupos con mínimo: cuántos habría apuntados contando esta reserva.
  const minimoGrupo =
    servicioSeleccionado?.permite_grupos &&
    (servicioSeleccionado?.cliente_min ?? 1) > 1
      ? servicioSeleccionado.cliente_min
      : null;
  const apuntadosConReserva =
    plazasHora != null
      ? (servicioSeleccionado?.cliente_max ?? 0) - plazasHora + form.num_clientes
      : null;
  const participantesMax = Math.min(
    servicioSeleccionado?.cliente_max || 1,
    plazasHora ?? Infinity
  );
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      num_clientes: Math.min(
        participantesMax,
        Math.max(participantesMin, prev.num_clientes)
      ),
    }));
  }, [form.servicio, participantesMin, participantesMax]);
  const cambiarParticipantes = (delta: number) =>
    setForm((prev) => ({
      ...prev,
      num_clientes: Math.min(
        participantesMax,
        Math.max(participantesMin, prev.num_clientes + delta)
      ),
    }));


  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  const mountTimestamp = useMemo(() => Date.now(), []);
  const logoUrl =
    typeof empresa?.logo === 'string' && empresa.logo.trim() !== ''
      ? empresa.logo.includes('?')
        ? empresa.logo
        : `${empresa.logo}?t=${mountTimestamp}`
      : null;

  const scrollFechasRef = useRef<HTMLDivElement>(null);
  const [dragFechas, setDragFechas] = useState({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
  });

  // ── Detecta breakpoint desktop (lg = 1024px) para mostrar más/menos items por defecto ──
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const serviciosVisiblesPorDefecto = isDesktop ? 4 : 3;
  const trabajadoresVisiblesPorDefecto = isDesktop ? 2 : 3;
  const horasVisiblesPorDefecto = isDesktop ? 12 : 8;

  const [mostrarTodosTrabajadores, setMostrarTodosTrabajadores] =
    useState(false);
  const [mostrarTodosServicios, setMostrarTodosServicios] = useState(false);
  const [mostrarTodasHoras, setMostrarTodasHoras] = useState(false);

  const servicioRef = useRef<HTMLDivElement>(null);
  const trabajadorRef = useRef<HTMLDivElement>(null);
  const fechaRef = useRef<HTMLDivElement>(null);
  const horaRef = useRef<HTMLDivElement>(null);
  const contactoRef = useRef<HTMLDivElement>(null);
  const [continuarSinLogin, setContinuarSinLogin] = useState(false);
  const {
    pending: hasPendingAppointments,
    citas: citasPendientes,
    refetch: refetchCitasPendientes,
  } = useCitasPendientesPorCliente(userEmail);
  const { accepted: consentAccepted, saveConsent } = useConsentimientoCliente(
    id_empresa,
    form.email
  );
  const showConsent = !!form.email && consentAccepted === false;

  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollFechasRef.current;
    if (!el) return;
    setDragFechas({
      isDown: true,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
    });
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragFechas.isDown) return;
    const el = scrollFechasRef.current;
    if (!el) return;
    el.scrollLeft =
      dragFechas.scrollLeft - (e.pageX - el.offsetLeft - dragFechas.startX);
  };
  const onMouseUp = () => setDragFechas((d) => ({ ...d, isDown: false }));

  useEffect(() => {
    const el = scrollFechasRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const cargarUsuario = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const user = data.user;
        if (user) {
          setUserEmail(user.email ?? null);
          Sentry.setUser({ email: user.email });
          setForm((prev) => ({
            ...prev,
            email: user.email || prev.email,
            telefono: user.user_metadata?.telefono || prev.telefono,
            nombre:
              user.user_metadata?.nombre_app || user.user_metadata?.full_name || prev.nombre,
          }));
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { action: 'cargar_usuario_booking' },
        });
      }
    };
    cargarUsuario();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user;
        if (!user) {
          Sentry.setUser(null);
          return;
        }
        setUserEmail(user.email ?? null);
        Sentry.setUser({ email: user.email });
        refetch();
        setForm((prev) => ({
          ...prev,
          email: user.email || prev.email,
          telefono: user.user_metadata?.telefono || prev.telefono,
          nombre:
            user.user_metadata?.nombre_app ??
            user.user_metadata?.full_name ??
            user.user_metadata?.name ??
            prev.nombre,
        }));
      }
    );
    return () => subscription.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!userEmail || loadingPerfiles) return;
    if (perfiles.some((p) => p.perfil === 'cliente'))
      setContinuarSinLogin(true);
  }, [userEmail, perfiles, loadingPerfiles]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener('clientRegistroCompletado', handler);
    return () =>
      window.removeEventListener('clientRegistroCompletado', handler);
  }, [refetch]);

  const getIniciales = (nombre: string) =>
    (nombre ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '?';

  const formatTelefono = (valor: string): string => {
    let limpio = valor.trim().replace(/\s+/g, '');
    if (limpio.startsWith('00')) limpio = '+' + limpio.slice(2);
    limpio = limpio.replace(/[^\d+]/g, '');
    if (limpio.includes('+'))
      limpio = '+' + limpio.replace(/\+/g, '').replace(/[^\d]/g, '');
    return limpio;
  };

  const isFechaValida = (fecha: string) => fecha >= hoyISO;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    const newValue = name === 'telefono' ? formatTelefono(value) : value;
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === 'fecha' || name === 'servicio' ? { hora: '' } : {}),
    }));
  };

  const seleccionarFecha = (fechaISO: string) => {
    setForm((prev) => ({ ...prev, fecha: fechaISO, hora: '' }));
  };

  const calcularHoraFin = (horaInicio: string, duracion: number): string => {
    const [h, m] = horaInicio.split(':').map(Number);
    const inicio = new Date();
    inicio.setHours(h, m, 0, 0);
    const fin = new Date(inicio.getTime() + duracion * 60000);
    return fin.toTimeString().substring(0, 5);
  };

  const handleSubmit = async () => {
    const { trabajador, servicio, telefono, fecha, hora, email, num_clientes } =
      form;
    const servicioSeleccionadoSubmit = servicios.find(
      (s) => s.id_servicio === servicio
    );

    if (
      !servicio ||
      !telefono ||
      !fecha ||
      !hora ||
      !email ||
      (servicioSeleccionadoSubmit?.requiere_trabajador && !trabajador)
    ) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setLoadingSubmit(true);

    Sentry.addBreadcrumb({
      category: 'booking',
      message: 'Intento de crear cita',
      level: 'info',
      data: { id_empresa, servicio, fecha, hora, email },
    });

    try {
      if (!servicioSeleccionadoSubmit) {
        toast.error('Servicio no válido');
        return;
      }
      const horaFin = calcularHoraFin(
        hora,
        servicioSeleccionadoSubmit.reserva_por_horas
          ? horasReserva * 60
          : servicioSeleccionadoSubmit.duracion_minutos
      );
      // Termina después de medianoche: la cita acaba el día siguiente.
      const cruzaMedianoche = horaFin <= hora.slice(0, 5);
      const fechaFinReserva = cruzaMedianoche
        ? format(addDays(new Date(`${fecha}T00:00:00`), 1), 'yyyy-MM-dd')
        : null;
      const recursoNumero =
        servicioSeleccionadoSubmit?.permite_grupos === true
          ? form.recurso_numero ?? 1
          : 1;

      const resultado = await crearCita({
        trabajador: servicioSeleccionadoSubmit.requiere_trabajador
          ? trabajador || null
          : null,
        servicio,
        telefono: formatTelefono(telefono),
        email,
        nombre: form.nombre,
        fecha,
        fechaFin: fechaFinReserva,
        desbordamiento: false,
        hora,
        horaFin,
        num_clientes,
        observaciones: form.nota ?? undefined,
        recurso_numero: recursoNumero,
      });

      if (!resultado.ok) {
        const msg =
          typeof resultado.error === 'string'
            ? resultado.error
            : 'Error al registrar la cita';

        Sentry.captureMessage(`Cita no creada: ${msg}`, {
          level: 'warning',
          tags: { action: 'crear_cita', id_empresa: id_empresa ?? '' },
          extra: { servicio, fecha, hora, email },
        });

        toast.error(msg);
        return;
      }

      Sentry.addBreadcrumb({
        category: 'booking',
        message: 'Cita creada con éxito',
        level: 'info',
        data: { id_empresa, servicio, fecha, hora },
      });

      toast.success('Cita registrada con éxito');

      // Solo clientes logueados registran push_token (guests nunca reciben push)
      if (userEmail) {
        const { data: userData } = await supabase.auth.getUser();
        if (userData.user?.id) {
          registrarPushToken(userData.user.id, userEmail);
        }
      }

      setForm((prev) => ({
        ...initialFormState,
        nombre: prev.nombre,
        email: prev.email,
        telefono: prev.telefono,
        consentimiento: prev.consentimiento,
      }));
      setCitaRegistrada(true);
      // Recargamos para que, si reserva otra seguida, el aviso de solape
      // tenga en cuenta la cita que acaba de crear
      refetchCitasPendientes();
      if (showConsent && form.consentimiento)
        await saveConsent(form.telefono || null);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'crear_cita', id_empresa: id_empresa ?? '' },
        extra: { servicio, fecha, hora, email },
      });
      console.error(err);
      toast.error('Error al registrar la cita');
    } finally {
      setLoadingSubmit(false);
    }
  };

  // ─── Steps progress ───────────────────────────────────────────────────────
  const step1Done = !!form.servicio;
  const step2Done =
    !!form.trabajador ||
    (step1Done && !servicioSeleccionado?.requiere_trabajador);
  const step3Done = !!form.fecha && !!form.hora;
  const step4Done = !!form.nombre && !!form.email && !!form.telefono;

  // ─── Aviso (no bloqueante) si la hora elegida pisa otra cita suya ─────────
  const citaSolapada = useMemo(() => {
    if (!form.hora || !form.fecha || !servicioSeleccionado) return null;
    const horaFin = calcularHoraFin(form.hora, duracionReserva);
    return (
      citasPendientes.find(
        (c) =>
          c.fecha_cita === form.fecha &&
          haySolape(form.hora, horaFin, c.hora_cita, c.hora_fin)
      ) ?? null
    );
  }, [
    form.fecha,
    form.hora,
    servicioSeleccionado,
    citasPendientes,
    duracionReserva,
  ]);

  const showSteps =
    continuarSinLogin ||
    (!!userEmail && perfiles.some((p) => p.perfil === 'cliente'));

  if (!errorEmpresa && (loadingEmpresa || !empresa || !splashDone)) {
    return (
      <EmpresaLoader
        logoUrl={empresa?.logo ?? logoPreview?.logo}
        nombre={empresa?.empresa ?? logoPreview?.nombre}
        onLogoLoad={() => setLogoCargado(true)}
        onLogoError={() => setSplashDone(true)}
      />
    );
  }

  if (
    !loadingEmpresa &&
    empresa &&
    ((empresa as any).tiene_plan_activo === false ||
      (empresa as any).plan_incluye_reservas === false)
  ) {
    return <EmpresaNoDisponible />;
  }
  // Tarjeta de servicio (lista plana y acordeón por categorías).
  const tarjetaServicio = (s: (typeof servicios)[number], destacado: boolean) => {
    const seleccionado = form.servicio === s.id_servicio;
    return (
      <button
        key={s.id_servicio}
        onClick={() => {
          setForm((prev) => ({
            ...prev,
            servicio: s.id_servicio,
            trabajador: s.requiere_trabajador ? '' : null,
            hora: '',
          }));
          setTimeout(
            () =>
              scrollTo(
                s.requiere_trabajador
                  ? trabajadorRef
                  : fechaRef
              ),
            200
          );
        }}
        className={`relative w-full h-[84px] text-left flex items-center justify-between px-3.5 rounded-xl border transition-all duration-200 ${
          seleccionado
            ? 'bg-[#103254]/5 dark:bg-blue-400/10 border-[#103254] dark:border-blue-400 shadow-sm'
            : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/20 hover:shadow-sm'
        }`}
      >
        {destacado && (
          <span className="absolute -top-2 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#288E7F] text-white shadow-sm">
            Más solicitado
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
              {s.servicio}
            </p>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            {s.reserva_por_horas
              ? s.horas_min === s.horas_max
                ? `${s.horas_min} h`
                : `${s.horas_min}–${s.horas_max} h`
              : `${s.duracion_minutos} min`}
            {s.precio != null && (
              <>
                {' '}
                ·{' '}
                <span className="font-medium text-gray-600 dark:text-gray-300">
                  {s.precio} €{s.reserva_por_horas && '/h'}
                </span>
              </>
            )}
            {s.precio_aplica && s.cliente_max > 1 && (
              <span className="ml-1">({s.precio_aplica})</span>
            )}
            {s.cliente_max > 1 && (
              <>
                {' '}
                ·{' '}
                {s.cliente_min === s.cliente_max
                  ? `${s.cliente_max} clientes`
                  : `${s.cliente_min}–${s.cliente_max} clientes`}
              </>
            )}
          </p>
          {s.dias_servicio &&
            s.dias_servicio.length > 0 && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 truncate">
                Disponible:{' '}
                {s.dias_servicio
                  .map((d) => d.dia_semana)
                  .join(', ')}
              </p>
            )}
        </div>
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ml-3 transition-all ${
            seleccionado
              ? 'bg-[#103254] border-[#103254]'
              : 'border-gray-200 dark:border-white/10'
          }`}
        >
          {seleccionado && (
            <Check className="w-2.5 h-2.5 text-white" />
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-[#f8f9fb] dark:bg-black">
      {/* Fondo decorativo solo en desktop: gradiente con acentos de marca, no negro plano */}
      <div
        className="hidden lg:block absolute inset-0 dark:hidden pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 12% 8%, rgba(40,142,127,0.10) 0%, transparent 50%), radial-gradient(ellipse at 88% 0%, rgba(16,50,84,0.10) 0%, transparent 50%)',
        }}
      />
      <div
        className="hidden lg:dark:block absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 12% 8%, rgba(40,142,127,0.18) 0%, transparent 50%), radial-gradient(ellipse at 88% 0%, rgba(16,50,84,0.35) 0%, transparent 55%), linear-gradient(180deg, #050807 0%, #0a1a16 30%, #0a0f14 65%, #000000 100%)',
        }}
      />

      <div className="flex-1 flex flex-col w-full relative z-10">
        {loadingEmpresa ? (
          <div className="flex justify-center items-center flex-1 min-h-[60vh]">
            <Loader2 className="animate-spin h-6 w-6 text-[#103254] dark:text-blue-400" />
          </div>
        ) : empresa?.reservas_online_citas ? (
          <>
            {/* ── HERO EMPRESA — a todo el ancho, como el resto de páginas de Bookoll ── */}
            <div className="relative text-white overflow-hidden">
              {/* Fondo modo claro — mismo acento verde/navy que Login, ajustado a un hero compacto */}
              <div
                className="absolute inset-0 dark:hidden"
                style={{
                  background: `
                    radial-gradient(ellipse at 15% 60%, rgba(40, 142, 127, 0.3) 0%, transparent 55%),
                    radial-gradient(ellipse at 85% 10%, rgba(16, 50, 84, 0.5) 0%, transparent 55%),
                    linear-gradient(170deg, #103254 0%, #143a5e 45%, #1a4a6e 100%)
                  `,
                }}
              />
              {/* Fondo modo oscuro */}
              <div
                className="absolute inset-0 hidden dark:block"
                style={{
                  background: `
                    radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.4) 0%, transparent 55%),
                    radial-gradient(ellipse at 85% 5%, rgba(16, 50, 84, 0.5) 0%, transparent 55%),
                    linear-gradient(170deg, #050807 0%, #0a1a16 30%, #123a30 65%, #103254 100%)
                  `,
                }}
              />

              <div className="relative py-2.5 text-center px-4">
                <p className="text-xs text-white/70">
                  ¿Tienes un negocio?{' '}
                  <a
                    href="/"
                    className="font-semibold text-white underline underline-offset-2 hover:opacity-80"
                  >
                    Inclúyelo
                  </a>
                </p>
              </div>

              <button
                onClick={() => (canGoBack ? navigate(-1) : navigate('/'))}
                className="absolute top-[46px] left-4 lg:left-6 w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors z-10"
              >
                <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </button>

              <div className="relative flex flex-col items-center gap-3 pt-6 pb-10 lg:pb-14 px-4 max-w-3xl mx-auto">
                {logoUrl && !logoError ? (
                  <img
                    src={logoUrl}
                    alt={empresa.empresa}
                    className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl object-cover border-2 border-white/20 shadow-lg"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/10 flex items-center justify-center text-2xl lg:text-3xl font-bold text-white/80">
                    {empresa.empresa?.charAt(0)}
                  </div>
                )}
                <div className="text-center">
                  <h1 className="text-lg lg:text-2xl font-bold text-white tracking-tight">
                    {empresa.empresa}
                  </h1>
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-1">
                    {empresa.ciudad && (
                      <p className="text-xs lg:text-sm text-white/50 flex items-center justify-center gap-1">
                        <MapPin className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                        {[empresa.localidad, empresa.ciudad]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                    {empresa.telefono && (
                      <a
                        href={`tel:${empresa.telefono}`}
                        className="text-xs lg:text-sm text-white/50 hover:text-white flex items-center justify-center gap-1 transition-colors"
                      >
                        <Phone className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                        {empresa.telefono}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
                <svg
                  viewBox="0 0 400 16"
                  preserveAspectRatio="none"
                  className="w-full h-3 lg:h-5"
                >
                  <path
                    d="M0,8 C100,16 300,0 400,8 L400,16 L0,16 Z"
                    className="fill-[#f8f9fb] dark:fill-black"
                  />
                </svg>
              </div>
            </div>

            {/* ── CONTENIDO DE RESERVA — sin fondo propio: el fondo de la página sigue de largo ── */}
            <div className="relative flex-1 flex flex-col w-full max-w-md lg:max-w-3xl mx-auto">
                {/* ── STEP INDICATOR ─────────────────────────────────────────── */}
                {showSteps && !citaRegistrada && (
                  <div className="px-6 py-4 flex items-center gap-1 lg:px-10 lg:pt-8">
                    <StepDot
                      active={!step1Done}
                      done={step1Done}
                      label="Servicio"
                    />
                    <StepLine done={step1Done} />
                    {servicioSeleccionado?.requiere_trabajador && (
                      <>
                        <StepDot
                          active={step1Done && !step2Done}
                          done={step2Done}
                          label="Profesional"
                        />
                        <StepLine done={step2Done} />
                      </>
                    )}
                    <StepDot
                      active={step2Done && !step3Done}
                      done={step3Done}
                      label="Fecha"
                    />
                    <StepLine done={step3Done} />
                    <StepDot
                      active={step3Done && !step4Done}
                      done={step4Done}
                      label="Datos"
                    />
                  </div>
                )}

                {/* ── CONTENIDO ──────────────────────────────────────────────── */}
                <div className="flex-1 px-5 py-5 space-y-8 lg:px-10 lg:py-8">
                {/* ── CITA REGISTRADA ──────────────────────────────────────── */}
                {citaRegistrada ? (
                  <div className="space-y-6">
                    {!continuarSinLogin && !userEmail && (
                      <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5 space-y-3 text-center">
                        <p className="text-sm font-semibold text-[#103254] dark:text-blue-400">
                          Para continuar con tu reserva
                        </p>
                        <div className="flex flex-col gap-2">
                          <LoginDialog
                            open={loginOpen}
                            setOpen={setLoginOpen}
                            context="clientBooking"
                          >
                            <Button
                              onClick={() => {
                                localStorage.setItem(
                                  'auth_origin',
                                  window.location.pathname
                                );
                                setLoginOpen(true);
                              }}
                              className="w-full bg-[#103254] text-white rounded-xl"
                            >
                              Iniciar sesión
                            </Button>
                          </LoginDialog>
                          <ClientRegisterChoiceDialog
                            open={registerOpen}
                            onOpenChange={setRegisterOpen}
                          />
                          <Button
                            variant="outline"
                            onClick={() => {
                              localStorage.setItem(
                                'auth_origin',
                                window.location.pathname
                              );
                              localStorage.setItem(
                                'after_register_return_to',
                                window.location.pathname
                              );
                              setRegisterOpen(true);
                            }}
                            className="w-full rounded-xl border-gray-200 dark:border-white/10 dark:text-gray-200"
                          >
                            Registrarse
                          </Button>
                          {!empresa?.permite_reservas_solo_clientes && (
                            <>
                              <div className="flex items-center gap-3 my-1">
                                <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  o
                                </span>
                                <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => setContinuarSinLogin(true)}
                                className="w-full rounded-xl border-[#103254]/30 dark:border-blue-400/30 text-[#103254] dark:text-blue-400"
                              >
                                Continuar sin iniciar sesión
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="rounded-2xl border border-green-100 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-6 text-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mx-auto mb-3">
                        <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">
                        ¡Cita registrada!
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Recibirás una confirmación en tu email.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5 space-y-3">
                      <p className="text-sm font-semibold text-[#103254] dark:text-blue-400">
                        {userEmail
                          ? 'Gestiona tus citas'
                          : 'Únete a Bookoll y podrás:'}
                      </p>
                      <ul className="space-y-2">
                        {[
                          'Cancelar tus citas online',
                          'Ver tu historial de reservas',
                          'Recibir recordatorios',
                          'Gestionar tu perfil',
                        ].map((item) => (
                          <li
                            key={item}
                            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                          >
                            <div className="w-4 h-4 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center shrink-0">
                              <Check className="w-2.5 h-2.5 text-[#103254] dark:text-blue-400" />
                            </div>
                            {item}
                          </li>
                        ))}
                      </ul>
                      {userEmail && (
                        <button
                          onClick={() => navigate('/panel-cliente/appointments')}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-[#103254] dark:text-blue-400 underline underline-offset-2 hover:opacity-80"
                        >
                          Ver mis citas <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    <Button
                      onClick={() => setCitaRegistrada(false)}
                      className="w-full bg-[#103254] text-white rounded-xl hover:bg-[#0d2742]"
                    >
                      Registrar otra cita
                    </Button>
                  </div>
                ) : !continuarSinLogin &&
                  (!userEmail ||
                    !perfiles.some((p) => p.perfil === 'cliente')) ? (
                  /* ── AUTH GATE ───────────────────────────────────────────── */
                  <div className="py-6 space-y-5">
                    <div className="text-center space-y-1">
                      <h2 className="text-base font-bold text-[#103254] dark:text-blue-400">
                        Para continuar con tu reserva
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Accede o créate una cuenta rápidamente
                      </p>
                    </div>

                    {empresa?.permite_reservas_solo_clientes && (
                      <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 px-4 py-3 text-xs text-center text-blue-700 dark:text-blue-300">
                        Esta empresa solo permite reservar si estás registrado.
                      </div>
                    )}

                    <div className="flex flex-col gap-2.5">
                      <LoginDialog
                        open={loginOpen}
                        setOpen={setLoginOpen}
                        context="clientBooking"
                      >
                        <Button
                          onClick={() => {
                            localStorage.setItem(
                              'auth_origin',
                              window.location.pathname
                            );
                            setLoginOpen(true);
                          }}
                          className="w-full bg-[#103254] text-white rounded-xl h-11"
                        >
                          Iniciar sesión
                        </Button>
                      </LoginDialog>
                      <ClientRegisterChoiceDialog
                        open={registerOpen}
                        onOpenChange={setRegisterOpen}
                      />
                      <Button
                        variant="outline"
                        onClick={() => {
                          localStorage.setItem(
                            'auth_origin',
                            window.location.pathname
                          );
                          localStorage.setItem(
                            'after_register_return_to',
                            window.location.pathname
                          );
                          setRegisterOpen(true);
                        }}
                        className="w-full rounded-xl border-gray-200 dark:border-white/10 dark:text-gray-200 h-11"
                      >
                        Crear cuenta
                      </Button>

                      {!empresa?.permite_reservas_solo_clientes && (
                        <>
                          <div className="flex items-center gap-3 my-1">
                            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              o
                            </span>
                            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => setContinuarSinLogin(true)}
                            className="w-full rounded-xl border-[#103254]/30 dark:border-blue-400/30 text-[#103254] dark:text-blue-400 h-11"
                          >
                            Continuar sin iniciar sesión
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* ── BOOKING FLOW ────────────────────────────────────────── */
                  <>
                    {userEmail && (
                      <div className="flex items-center gap-3 rounded-2xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 shadow-sm px-3 py-2.5">
                        <div className="relative shrink-0">
                          <div className="w-10 h-10 rounded-full bg-[#103254] dark:bg-blue-500 text-white flex items-center justify-center text-sm font-bold uppercase">
                            {userEmail.charAt(0)}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-black" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {userEmail}
                          </p>
                          {hasPendingAppointments ? (
                            <button
                              onClick={() => navigate('/panel-cliente/appointments')}
                              className="mt-0.5 inline-flex items-center gap-0.5 text-xs font-medium text-[#288E7F] dark:text-emerald-400 hover:underline underline-offset-2"
                            >
                              Tienes citas pendientes
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                              Reservando con tu cuenta
                            </p>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            await logoutUser();
                            Sentry.setUser(null);
                            setUserEmail(null);
                            setForm(initialFormState);
                            setCitaRegistrada(false);
                            setContinuarSinLogin(false);
                            toast.success('Sesión cerrada');
                          }}
                          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Cerrar sesión"
                        >
                          <LogOut className="w-3.5 h-3.5" />
                          Salir
                        </button>
                      </div>
                    )}

                    {/* ── SERVICIOS ──────────────────────────────────────────── */}
                    <section ref={servicioRef}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400">
                          Servicio
                        </h3>
                        <button
                          onClick={() =>
                            navigate(
                              `/${empresa?.categoria_slug}/${empresa?.ciudad_slug}/${empresa?.empresa_slug}`,
                              { state: { fromClientBooking: true } }
                            )
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#103254] dark:text-blue-400 bg-[#103254]/5 dark:bg-blue-400/10 border border-[#103254]/15 dark:border-blue-400/20 hover:bg-[#103254]/10 dark:hover:bg-blue-400/20 transition-colors"
                        >
                          <Info className="w-3.5 h-3.5" />
                          Sobre la empresa
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {agruparPorCategoria ? (
                        <div className="space-y-2">
                          {gruposServicios.map((g) => {
                            const abierta =
                              categoriasAbiertas[g.clave] ??
                              (g.clave === gruposServicios[0]?.clave ||
                                g.servicios.some(
                                  (s) => s.id_servicio === form.servicio
                                ));
                            return (
                              <div
                                key={g.clave}
                                className="rounded-2xl border border-gray-100 dark:border-white/10 bg-white/60 dark:bg-white/[0.02]"
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCategoriasAbiertas((prev) => ({
                                      ...prev,
                                      [g.clave]: !abierta,
                                    }))
                                  }
                                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                                >
                                  <span className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                                    {g.nombre}
                                  </span>
                                  <span className="flex items-center gap-2 shrink-0 text-xs text-gray-400 dark:text-gray-500">
                                    {g.servicios.length}{' '}
                                    {g.servicios.length === 1 ? 'servicio' : 'servicios'}
                                    <ChevronDown
                                      className={`w-4 h-4 transition-transform duration-200 ${
                                        abierta ? 'rotate-180' : ''
                                      }`}
                                    />
                                  </span>
                                </button>
                                {abierta && (
                                  <div className="px-2 pb-2 pt-1 space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
                                    {g.servicios.map((s) =>
                                      tarjetaServicio(
                                        s,
                                        s.id_servicio === serviciosOrdenados[0]?.id_servicio
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
                          {(mostrarTodosServicios
                            ? serviciosOrdenados
                            : serviciosOrdenados.slice(0, serviciosVisiblesPorDefecto)
                          ).map((s, idx) => tarjetaServicio(s, idx === 0))}

                          {servicios.length > serviciosVisiblesPorDefecto && (
                            <button
                              onClick={() => setMostrarTodosServicios((p) => !p)}
                              className="w-full py-2 text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline lg:col-span-2"
                            >
                              {mostrarTodosServicios
                                ? 'Ver menos'
                                : `Ver todos los servicios (${servicios.length})`}
                            </button>
                          )}
                        </div>
                      )}
                    </section>

                    {/* ── PROFESIONALES ──────────────────────────────────────── */}
                    {servicioSeleccionado?.requiere_trabajador && (
                      <section ref={trabajadorRef}>
                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400 mb-3">
                          Profesional
                        </h3>
                        <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
                          {(mostrarTodosTrabajadores
                            ? trabajadores
                            : trabajadores.slice(0, trabajadoresVisiblesPorDefecto)
                          ).map((t, idx, lista) => {
                            const seleccionado = form.trabajador === t.id_usuario;
                            // En dos columnas, el último de una lista impar ocupa toda la fila.
                            const anchoCompleto =
                              lista.length % 2 === 1 && idx === lista.length - 1;
                            return (
                              <button
                                key={t.id_usuario}
                                onClick={() => {
                                  setForm((prev) => ({
                                    ...prev,
                                    trabajador: t.id_usuario,
                                    hora: '',
                                  }));
                                  setTimeout(() => scrollTo(fechaRef), 200);
                                }}
                                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${
                                  anchoCompleto ? 'lg:col-span-2' : ''
                                } ${
                                  seleccionado
                                    ? 'bg-[#103254]/5 dark:bg-blue-400/10 border-[#103254] dark:border-blue-400 shadow-sm'
                                    : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/20'
                                }`}
                              >
                                {t.foto ? (
                                  <img
                                    src={t.foto}
                                    alt={t.nombre}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-white/10 shadow-sm shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center text-sm font-bold text-[#103254] dark:text-blue-400 shrink-0">
                                    {getIniciales(t.nombre)}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800 dark:text-white">
                                    {t.nombre}
                                  </p>
                                  {seleccionado && (
                                    <p className="text-xs text-[#103254] dark:text-blue-400 font-medium">
                                      Seleccionado
                                    </p>
                                  )}
                                </div>
                                <div
                                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                    seleccionado
                                      ? 'bg-[#103254] border-[#103254]'
                                      : 'border-gray-200 dark:border-white/10'
                                  }`}
                                >
                                  {seleccionado && (
                                    <Check className="w-2.5 h-2.5 text-white" />
                                  )}
                                </div>
                              </button>
                            );
                          })}
                          {trabajadores.length > trabajadoresVisiblesPorDefecto && (
                            <button
                              onClick={() =>
                                setMostrarTodosTrabajadores((p) => !p)
                              }
                              className="w-full py-2 text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline lg:col-span-2"
                            >
                              {mostrarTodosTrabajadores
                                ? 'Ver menos'
                                : `Ver todos (${trabajadores.length})`}
                            </button>
                          )}
                        </div>
                      </section>
                    )}

                    {/* ── FECHA ──────────────────────────────────────────────── */}
                    {form.servicio &&
                      (!servicioSeleccionado?.requiere_trabajador ||
                        form.trabajador) && (
                        <section ref={fechaRef}>
                          {porHoras && (
                            <div className="mb-5">
                              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400 mb-3">
                                Duración
                              </h3>
                              <div className="flex items-center gap-3">
                                <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5">
                                  <button
                                    type="button"
                                    onClick={() => cambiarHoras(-1)}
                                    disabled={horasReserva <= horasMin}
                                    className="w-10 h-10 flex items-center justify-center text-[#103254] dark:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
                                    aria-label="Menos horas"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="min-w-[56px] text-center text-sm font-semibold text-gray-800 dark:text-white tabular-nums">
                                    {horasReserva} h
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => cambiarHoras(1)}
                                    disabled={horasReserva >= horasMax}
                                    className="w-10 h-10 flex items-center justify-center text-[#103254] dark:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
                                    aria-label="Más horas"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {form.hora && (
                                    <p className="font-semibold text-gray-800 dark:text-white tabular-nums">
                                      {form.hora.slice(0, 5)} –{' '}
                                      {calcularHoraFin(form.hora, duracionReserva)}
                                    </p>
                                  )}
                                  {servicioSeleccionado?.precio != null && (
                                    <p>
                                      {servicioSeleccionado.precio} €/h ·{' '}
                                      <span className="font-semibold text-gray-800 dark:text-white">
                                        {precioTotalReserva} €
                                      </span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex items-baseline justify-between mb-3">
                            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400">
                              Fecha
                            </h3>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              Hasta {empresa?.dias_visibles_reserva ?? 14} días
                            </span>
                          </div>

                          <div
                            ref={scrollFechasRef}
                            onMouseDown={onMouseDown}
                            onMouseMove={onMouseMove}
                            onMouseUp={onMouseUp}
                            onMouseLeave={onMouseUp}
                            className="flex gap-2 overflow-x-auto no-scrollbar pb-1"
                          >
                            {diasDisponibles.map((d) => {
                              const seleccionado = form.fecha === d.fechaISO;
                              const totalHorarios =
                                horariosPorDia[d.fechaISO]?.length || 0;
                              const mostrarMes =
                                (empresa?.dias_visibles_reserva ?? 14) > 30;
                              const mesAbreviado = format(
                                new Date(d.fechaISO),
                                'MMM',
                                { locale: es }
                              );

                              return (
                                <button
                                  key={d.fechaISO}
                                  onClick={() => {
                                    if (totalHorarios > 0) {
                                      seleccionarFecha(d.fechaISO);
                                      setTimeout(() => scrollTo(horaRef), 200);
                                    }
                                  }}
                                  disabled={totalHorarios === 0}
                                  className={`flex flex-col items-center justify-center h-[72px] min-w-[52px] rounded-xl border transition-all duration-200 ${
                                    seleccionado
                                      ? 'bg-[#103254] border-[#103254] text-white shadow-md'
                                      : totalHorarios > 0
                                      ? 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-[#103254]/60 dark:hover:border-blue-400/60 hover:shadow-sm'
                                      : 'bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                  }`}
                                >
                                  <span className="text-[10px] font-semibold capitalize opacity-70">
                                    {d.diaSemana}
                                  </span>
                                  <span className="text-lg font-bold leading-none mt-0.5">
                                    {d.diaNumero}
                                  </span>
                                  {mostrarMes && (
                                    <span
                                      className={`text-[9px] uppercase mt-0.5 ${
                                        seleccionado
                                          ? 'text-white/60'
                                          : 'text-[#103254]/50 dark:text-blue-400/50'
                                      }`}
                                    >
                                      {mesAbreviado}
                                    </span>
                                  )}
                                  {totalHorarios > 0 && (
                                    <div
                                      className={`w-1 h-1 rounded-full mt-1 ${
                                        seleccionado
                                          ? 'bg-white'
                                          : totalHorarios <= 4
                                          ? 'bg-orange-400'
                                          : 'bg-green-400'
                                      }`}
                                    />
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          {/* ── HORA ─────────────────────────────────────────── */}
                          {isFechaValida(form.fecha) && (
                            <div className="mt-5" ref={horaRef}>
                              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400 mb-3">
                                Hora
                              </h3>
                              {horasDisponibles.length > 0 ? (
                                <>
                                  <div className="grid grid-cols-4 lg:grid-cols-6 gap-2">
                                    {Object.entries(
                                      horasDisponibles.reduce<
                                        Record<string, HorarioItem[]>
                                      >((acc, h) => {
                                        if (!acc[h.hora]) acc[h.hora] = [];
                                        acc[h.hora].push(h);
                                        return acc;
                                      }, {})
                                    )
                                      .slice(0, mostrarTodasHoras ? undefined : horasVisiblesPorDefecto)
                                      .map(([hora, recursos]) => {
                                        const horaFormateada = hora.slice(0, 5);
                                        const totalRecursos = recursos.length;
                                        const seleccionada = form.hora === hora;
                                        const admiteGrupos =
                                          servicioSeleccionado?.permite_grupos ===
                                          true;

                                        return (
                                          <div key={hora} className="space-y-1">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                if (totalRecursos === 1) {
                                                  setForm((prev) => ({
                                                    ...prev,
                                                    hora,
                                                    recurso_numero:
                                                      recursos[0].recurso_numero,
                                                  }));
                                                  setTimeout(
                                                    () => scrollTo(contactoRef),
                                                    200
                                                  );
                                                } else {
                                                  setForm((prev) => ({
                                                    ...prev,
                                                    hora,
                                                  }));
                                                }
                                              }}
                                              className={`w-full h-11 rounded-xl border text-sm font-semibold transition-all duration-200 ${
                                                seleccionada
                                                  ? 'bg-[#103254] text-white border-[#103254] shadow-md'
                                                  : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:border-[#103254]/60 dark:hover:border-blue-400/60 hover:shadow-sm'
                                              }`}
                                            >
                                              {horaFormateada}
                                              {totalRecursos > 1 && (
                                                <span
                                                  className={`block text-[9px] font-normal leading-tight ${
                                                    seleccionada
                                                      ? 'text-white/70'
                                                      : 'text-gray-400 dark:text-gray-500'
                                                  }`}
                                                >
                                                  {totalRecursos} recursos
                                                </span>
                                              )}
                                              {totalRecursos === 1 &&
                                                recursos[0].plazas_disponibles >
                                                  1 && (
                                                  <span
                                                    className={`block text-[9px] font-normal leading-tight ${
                                                      seleccionada
                                                        ? 'text-white/70'
                                                        : 'text-gray-400 dark:text-gray-500'
                                                    }`}
                                                  >
                                                    {
                                                      recursos[0]
                                                        .plazas_disponibles
                                                    }{' '}
                                                    plazas
                                                  </span>
                                                )}
                                            </button>

                                            {admiteGrupos &&
                                              totalRecursos > 1 &&
                                              seleccionada && (
                                                <div className="col-span-4 space-y-1 mt-1">
                                                  {recursos.map((r) => (
                                                    <button
                                                      key={r.recurso_numero}
                                                      type="button"
                                                      onClick={() => {
                                                        setForm((prev) => ({
                                                          ...prev,
                                                          hora,
                                                          recurso_numero:
                                                            r.recurso_numero,
                                                        }));
                                                        setTimeout(
                                                          () =>
                                                            scrollTo(contactoRef),
                                                          200
                                                        );
                                                      }}
                                                      className={`w-full flex justify-between items-center px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                                                        form.recurso_numero ===
                                                        r.recurso_numero
                                                          ? 'bg-[#103254] text-white border-[#103254]'
                                                          : 'bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/20'
                                                      }`}
                                                    >
                                                      <span>
                                                        Recurso {r.recurso_numero}
                                                      </span>
                                                      <span className="text-xs opacity-70">
                                                        {r.plazas_disponibles}{' '}
                                                        plaza
                                                        {r.plazas_disponibles > 1
                                                          ? 's'
                                                          : ''}
                                                      </span>
                                                    </button>
                                                  ))}
                                                </div>
                                              )}
                                          </div>
                                        );
                                      })}
                                  </div>

                                  {horasDisponibles.length > horasVisiblesPorDefecto && (
                                    <button
                                      onClick={() =>
                                        setMostrarTodasHoras((p) => !p)
                                      }
                                      className="w-full mt-3 py-2 text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline"
                                    >
                                      {mostrarTodasHoras
                                        ? 'Ver menos'
                                        : 'Ver más horas'}
                                    </button>
                                  )}

                                  {horasMadrugada.length > 0 && (
                                    <div className="mt-4">
                                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                                        Madrugada del{' '}
                                        {format(
                                          new Date(`${fechaSiguiente}T00:00:00`),
                                          'EEEE d',
                                          { locale: es }
                                        )}
                                      </p>
                                      <div className="grid grid-cols-4 lg:grid-cols-6 gap-2">
                                        {(verMadrugada
                                          ? horasMadrugada
                                          : horasMadrugada.slice(0, 3)
                                        ).map(([hora, recursos]) => (
                                          <button
                                            key={hora}
                                            type="button"
                                            onClick={() => {
                                              setForm((prev) => ({
                                                ...prev,
                                                fecha: fechaSiguiente,
                                                hora,
                                                recurso_numero:
                                                  recursos.length === 1
                                                    ? recursos[0].recurso_numero
                                                    : prev.recurso_numero,
                                              }));
                                              setVerMadrugada(false);
                                              if (recursos.length === 1)
                                                setTimeout(
                                                  () => scrollTo(contactoRef),
                                                  200
                                                );
                                            }}
                                            className="w-full h-11 rounded-xl border border-dashed border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:border-[#103254]/60 dark:hover:border-blue-400/60 transition-all duration-200"
                                          >
                                            {hora.slice(0, 5)}
                                            <span className="block text-[9px] font-normal leading-tight text-gray-400 dark:text-gray-500">
                                              +1 día
                                            </span>
                                          </button>
                                        ))}
                                      </div>
                                      {horasMadrugada.length > 3 && (
                                        <button
                                          type="button"
                                          onClick={() => setVerMadrugada((v) => !v)}
                                          className="w-full mt-2 py-2 text-xs font-semibold text-[#103254] dark:text-blue-400 hover:underline"
                                        >
                                          {verMadrugada
                                            ? 'Ver menos'
                                            : `Ver más de madrugada (${horasMadrugada.length})`}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-400 dark:text-gray-500 py-2">
                                  No hay horarios disponibles para este día.
                                </p>
                              )}
                            </div>
                          )}
                        </section>
                      )}

                    {/* ── CONTACTO ───────────────────────────────────────────── */}
                    {form.servicio &&
                      form.fecha &&
                      form.hora &&
                      (!servicioSeleccionado?.requiere_trabajador ||
                        form.trabajador) && (
                        <section ref={contactoRef} className="space-y-4">
                          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#103254] dark:text-blue-400">
                            Tus datos
                          </h3>

                          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
                            <div>
                              <Label
                                htmlFor="nombre"
                                className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block"
                              >
                                Nombre
                              </Label>
                              <Input
                                type="text"
                                name="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                                className="rounded-xl border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 dark:text-white focus:bg-white dark:focus:bg-white/10 focus:border-[#103254] dark:focus:border-blue-400 transition-colors"
                              />
                            </div>
                            <div>
                              <Label
                                htmlFor="telefono"
                                className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block"
                              >
                                Teléfono
                              </Label>
                              <Input
                                type="tel"
                                name="telefono"
                                value={form.telefono}
                                onChange={handleChange}
                                required
                                className="rounded-xl border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 dark:text-white focus:bg-white dark:focus:bg-white/10 focus:border-[#103254] dark:focus:border-blue-400 transition-colors"
                              />
                            </div>

                            {(servicioSeleccionado?.cliente_max ?? 0) > 1 && (
                              <div>
                                <Label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                                  Nº de participantes
                                </Label>
                                <div className="flex items-center gap-3">
                                  <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5">
                                    <button
                                      type="button"
                                      onClick={() => cambiarParticipantes(-1)}
                                      disabled={form.num_clientes <= participantesMin}
                                      className="w-10 h-10 flex items-center justify-center text-[#103254] dark:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
                                      aria-label="Menos participantes"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="min-w-[56px] text-center text-sm font-semibold text-gray-800 dark:text-white tabular-nums">
                                      {form.num_clientes}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => cambiarParticipantes(1)}
                                      disabled={form.num_clientes >= participantesMax}
                                      className="w-10 h-10 flex items-center justify-center text-[#103254] dark:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
                                      aria-label="Más participantes"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {servicioSeleccionado?.precio != null && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      Total:{' '}
                                      <span className="font-semibold text-gray-800 dark:text-white">
                                        {precioTotalReserva} €
                                      </span>
                                    </p>
                                  )}
                                </div>
                                {minimoGrupo != null &&
                                  (apuntadosConReserva == null ||
                                    apuntadosConReserva < minimoGrupo) && (
                                    <div className="mt-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                      Este servicio necesita un mínimo de{' '}
                                      {minimoGrupo} participantes entre todas las
                                      reservas.
                                      {apuntadosConReserva != null &&
                                        ` Contándote, seríais ${apuntadosConReserva}.`}{' '}
                                      Si no se llega al mínimo, lo más seguro es que
                                      se cancele.
                                    </div>
                                  )}
                                {plazasHora != null &&
                                  plazasHora < (servicioSeleccionado?.cliente_max ?? 0) && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                      {plazasHora === 1
                                        ? 'Queda 1 plaza a esta hora'
                                        : `Quedan ${plazasHora} plazas a esta hora`}
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>

                          <div>
                            <Label
                              htmlFor="email"
                              className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block"
                            >
                              Email
                            </Label>
                            <Input
                              type="email"
                              name="email"
                              value={form.email}
                              onChange={handleChange}
                              required
                              className="w-full rounded-xl border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 dark:text-white focus:bg-white dark:focus:bg-white/10 focus:border-[#103254] dark:focus:border-blue-400 transition-colors"
                            />
                          </div>

                          {form.nota === undefined ? (
                            <button
                              type="button"
                              onClick={() =>
                                setForm((prev) => ({ ...prev, nota: '' }))
                              }
                              className="text-xs text-gray-400 dark:text-gray-500 hover:text-[#103254] dark:hover:text-blue-400 transition-colors flex items-center gap-1"
                            >
                              + Añadir nota opcional
                            </button>
                          ) : (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <Label
                                  htmlFor="nota"
                                  className="text-xs font-medium text-gray-500 dark:text-gray-400"
                                >
                                  Nota (opcional)
                                </Label>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((prev) => {
                                      const { nota, ...rest } = prev;
                                      return { ...rest, nota: undefined };
                                    })
                                  }
                                  className="text-xs text-red-400 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                                >
                                  Ocultar
                                </button>
                              </div>
                              <textarea
                                id="nota"
                                name="nota"
                                value={form.nota}
                                onChange={handleChange}
                                placeholder="Detalles adicionales..."
                                className="w-full rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 dark:text-white focus:bg-white dark:focus:bg-white/10 focus:border-[#103254] dark:focus:border-blue-400 focus:outline-none p-3 text-sm resize-none transition-colors"
                                rows={3}
                              />
                            </div>
                          )}

                          {showConsent && (
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                              <Switch
                                id="consentimiento"
                                checked={form.consentimiento}
                                onCheckedChange={(checked) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    consentimiento: checked,
                                  }))
                                }
                              />
                              <Label
                                htmlFor="consentimiento"
                                className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed cursor-pointer"
                              >
                                Acepto el tratamiento de mis datos conforme a la{' '}
                                <button
                                  type="button"
                                  onClick={() => openExternalLink('https://bookoll.com/privacy-policy')}
                                  className="text-[#103254] dark:text-blue-400 underline font-medium text-xs leading-relaxed"
                                >
                                  Política de Privacidad
                                </button>
                              </Label>
                            </div>
                          )}

                          {form.hora && (
                            <div className="rounded-xl bg-[#103254]/5 dark:bg-blue-400/10 border border-[#103254]/10 dark:border-blue-400/20 p-4 space-y-1.5">
                              <p className="text-xs font-bold uppercase tracking-wider text-[#103254] dark:text-blue-400 mb-2">
                                Resumen
                              </p>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Servicio
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-white">
                                  {servicioSeleccionado?.servicio}
                                </span>
                              </div>
                              {form.trabajador &&
                                trabajadores.find(
                                  (t) => t.id_usuario === form.trabajador
                                ) && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Profesional
                                    </span>
                                    <span className="font-semibold text-gray-800 dark:text-white">
                                      {
                                        trabajadores.find(
                                          (t) => t.id_usuario === form.trabajador
                                        )?.nombre
                                      }
                                    </span>
                                  </div>
                                )}
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Fecha
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-white">
                                  {format(new Date(form.fecha), "d 'de' MMMM", {
                                    locale: es,
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-gray-400">
                                  Hora
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-white">
                                  {porHoras
                                    ? `${form.hora.slice(0, 5)} – ${calcularHoraFin(
                                        form.hora,
                                        duracionReserva
                                      )} (${horasReserva} h)`
                                    : form.hora.slice(0, 5)}
                                </span>
                              </div>
                              {servicioSeleccionado?.precio != null && (
                                <>
                                  <div className="flex justify-between text-sm pt-1 border-t border-[#103254]/10 dark:border-blue-400/20 mt-1">
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Total
                                    </span>
                                    <span className="font-bold text-[#103254] dark:text-blue-400">
                                      {precioTotalReserva} €
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-gray-400 dark:text-gray-500 text-right mt-0.5">
                                    Pagarás directamente en el local
                                  </p>
                                </>
                              )}
                            </div>
                          )}

                          {/* Aviso no bloqueante: la hora elegida pisa otra cita suya */}
                          {citaSolapada && (
                            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-300">
                              <p className="font-semibold mb-1">
                                Ya tienes una cita a esa hora
                              </p>
                              <p className="text-xs leading-relaxed">
                                El{' '}
                                {format(
                                  new Date(citaSolapada.fecha_cita),
                                  "d 'de' MMMM",
                                  { locale: es }
                                )}{' '}
                                a las {citaSolapada.hora_cita.slice(0, 5)}
                                {citaSolapada.nombre_empresa
                                  ? ` en ${citaSolapada.nombre_empresa}`
                                  : ''}
                                . Puedes reservar igualmente si estás seguro.
                              </p>
                            </div>
                          )}

                          <Button
                            onClick={handleSubmit}
                            className="w-full bg-[#103254] text-white hover:bg-[#0d2742] rounded-xl h-12 text-sm font-semibold shadow-lg shadow-[#103254]/20 transition-all"
                            disabled={
                              loadingSubmit ||
                              (showConsent && !form.consentimiento)
                            }
                          >
                            {loadingSubmit ? (
                              <>
                                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                                Reservando...
                              </>
                            ) : (
                              'Confirmar reserva'
                            )}
                          </Button>
                        </section>
                      )}
                  </>
                )}
              </div>

              {/* ── FOOTER: dirección — sin superficie propia, solo un separador sutil ── */}
              {empresa && empresa.direccion && (
                <footer className="mt-auto py-4 px-5 border-t border-gray-200/50 dark:border-white/5">
                  <div className="flex justify-center items-center text-xs text-gray-400 dark:text-gray-500">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        empresa.direccion
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 hover:text-[#103254] dark:hover:text-blue-400 transition-colors"
                    >
                      <MapPin className="w-3 h-3" />
                      <span className="font-medium">{empresa.direccion}</span>
                    </a>
                  </div>
                </footer>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center px-6 min-h-[60vh]">
            <div className="rounded-2xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 p-6 text-center text-sm w-full">
              Esta empresa no permite reservas online en este momento.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientBooking;